import {hpcAttrs} from '@github-ui/hpc'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
import {useQueryClient} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useNavigate} from '@github-ui/use-navigate'
import {Spinner} from '@primer/react'
import {type RefObject, useCallback, useEffect, useRef} from 'react'

import {ERRORS} from './constants/errors'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {
  useIssueCreateActions,
  useIssueCreateComposition,
  useIssueCreateMetadata,
} from './contexts/IssueCreateDataContext'
import type {CreateIssueFragment} from './create-issue-fragment-types'
import {CreateDuplicateIssueForm} from './CreateDuplicateIssueForm'
import styles from './CreateIssue.module.css'
import {type CreateIssueCallbackProps, CreateIssueForm, CreateIssueFormBase} from './CreateIssueForm'
import {fetchTemplate, runLatestTemplateRequest, TEMPLATE_STALE_TIME, templateQueryKey} from './fetch-template'
import type {DuplicateDetectionState} from './hooks/use-duplicate-detection'
import {useOnCreateSuccess} from './hooks/use-on-create-success'
import {RepositoryAndTemplatePicker} from './RepositoryAndTemplatePicker'
import type {TemplateListSharedProps} from './TemplateList'
import {TemplateListInternal, type TemplateListPaneFragment} from './TemplateListPane'
import {TemplatePickerButton} from './TemplatePickerButton'
import {getSelectedTemplate, useHandleTemplateChange} from './use-handle-template-change'
import {DisplayMode, getRepositoryDisplayMode} from './utils/display-mode'
import {getBlankIssue, IssueCreationKind} from './utils/model'
import type {IssueCreateValueTypes} from './utils/template-args'

export type CreateIssueProps = {
  currentRepository: CreateIssueFragment | null | 'unset'
  topRepositories: RepositoryPickerRepository[] | null | 'unset'
  navigate: (url: string) => void
  issueFormRef: RefObject<IssueFormRef | null>
  onSafeClose?: () => void
  duplicateDetection?: DuplicateDetectionState
  requiresRelayEnvironment?: boolean
} & CreateIssueCallbackProps &
  TemplateListSharedProps

export const CreateIssue = ({
  topRepositories,
  navigate,
  currentRepository,
  onCreateSuccess,
  issueFormRef,
  duplicateDetection,
  requiresRelayEnvironment = true,
  ...props
}: CreateIssueProps) => {
  // we're waiting to load the current repo
  // OR we don't have a current repo and we're waiting to load top repos
  const isLoadingRepositoryData =
    (currentRepository !== 'unset' && currentRepository === null) ||
    (topRepositories !== 'unset' && topRepositories === null)

  const {repository, setRepository, template} = useIssueCreateMetadata()
  const {optionConfig, displayMode, initialDefaultDisplayMode, setDisplayMode, setCreateMoreCreatedPath} =
    useIssueCreateConfigContext()

  const isRepositorySelectedInTemplatePicker =
    displayMode === DisplayMode.TemplatePicker && optionConfig.showRepositoryPicker && repository !== undefined
  const shouldReadCurrentRepositoryFragment = currentRepository !== 'unset' && !isRepositorySelectedInTemplatePicker

  const data = shouldReadCurrentRepositoryFragment ? currentRepository : null

  const {addToast} = useToastContext()
  const queryClient = useQueryClient()
  // Guards against out-of-order responses when the user clicks templates in quick succession:
  // only the latest request is allowed to apply its result.
  const latestTemplateRequestRef = useRef(0)
  const {title, body, setBody} = useIssueCreateComposition()
  const {setNewTitle, resetMetadata, clearSessionData} = useIssueCreateActions()
  const initialRef = useRef({
    isInitialRender: true,
    values: {
      resetMetadata,
      initialDefaultDisplayMode,
      repository,
      setDisplayMode,
    },
  })

  useEffect(() => {
    // Only run once on initial render
    if (initialRef.current.isInitialRender) {
      initialRef.current.isInitialRender = false

      const {
        resetMetadata: resetMetadataRef,
        initialDefaultDisplayMode: initialDefaultDisplayModeRef,
        repository: repositoryRef,
        setDisplayMode: setDisplayModeRef,
      } = initialRef.current.values

      if (initialDefaultDisplayModeRef === DisplayMode.IssueDuplication) return

      resetMetadataRef()

      const repositoryDisplayMode = repositoryRef ? getRepositoryDisplayMode(repositoryRef) : undefined
      setDisplayModeRef(repositoryDisplayMode ?? initialDefaultDisplayModeRef)
    }
  }, [])

  const resetDuplicateDetection = duplicateDetection?.resetForNewSession

  const onRepositorySelected = useCallback(
    (selectedRepository: RepositoryPickerRepository | undefined) => {
      if (selectedRepository) {
        if (repository?.id === selectedRepository.id) {
          return
        }

        latestTemplateRequestRef.current += 1

        if (repository) {
          // We are only clearing metadata because we want to persist title & body across repo changes incase we are selecting blank issues
          resetMetadata({
            repository: initialRef.current.values.repository,
            selectedRepository,
          })
        }

        const repositoryDisplayMode = getRepositoryDisplayMode(selectedRepository)
        if (repositoryDisplayMode) setDisplayMode(repositoryDisplayMode)

        setRepository(selectedRepository)
        setCreateMoreCreatedPath({
          owner: selectedRepository.owner.login,
          repo: selectedRepository.name,
          number: undefined,
        })
      }
    },
    [resetMetadata, repository, setCreateMoreCreatedPath, setRepository, setDisplayMode],
  )

  const handleTemplateChange = useHandleTemplateChange({
    optionConfig,
    repository,
    navigate,
    setDisplayMode,
  })

  const appendTitleToTemplate = optionConfig.issueCreateArguments?.initialValues?.appendTitleToTemplate

  const navigateRouter = useNavigate()
  const onTemplateSelected = useCallback(
    (filename: string, kind: IssueCreationKind) => {
      if (!repository) {
        return
      }

      if (
        (repository?.viewerInteractionLimitReasonHTML && repository.viewerInteractionLimitReasonHTML.length > 0) ||
        repository?.viewerCanCreateIssues === false
      ) {
        const redirectUrl = `/${repository.owner.login}/${repository.name}/issues/new`
        ;(navigate === noop ? navigateRouter : navigate)(redirectUrl)
        return
      }

      latestTemplateRequestRef.current += 1

      // Contact links will redirect to a new page, so we don't want to set the template
      if (kind === IssueCreationKind.ContactLink) {
        return
      }
      let extraArgs: IssueCreateValueTypes | undefined = undefined
      if (appendTitleToTemplate) {
        extraArgs = {
          appendTitleToTemplate,
        }
      }
      if (kind === IssueCreationKind.BlankIssue) {
        resetDuplicateDetection?.()
        const selectedTemplate = getBlankIssue()
        handleTemplateChange(selectedTemplate, extraArgs, true)
        return
      }

      void runLatestTemplateRequest(
        latestTemplateRequestRef,
        () =>
          queryClient.fetchQuery({
            queryKey: templateQueryKey(repository.owner.login, repository.name, filename),
            queryFn: ({signal}) => fetchTemplate(repository.owner.login, repository.name, filename, signal),
            staleTime: TEMPLATE_STALE_TIME,
            retry: false,
          }),
        node => {
          if (!node) return

          const fetchedTemplate = getSelectedTemplate(node)
          if (fetchedTemplate) {
            resetDuplicateDetection?.()
            handleTemplateChange(fetchedTemplate, extraArgs, true)
          }
        },
        () => {
          reportError(new Error(ERRORS.unableToLoadSelectedTemplate))

          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.unableToLoadSelectedTemplate,
          })
        },
      )
    },
    [
      repository,
      appendTitleToTemplate,
      navigate,
      navigateRouter,
      resetDuplicateDetection,
      handleTemplateChange,
      addToast,
      queryClient,
    ],
  )

  const onCreateSuccessInternal = useOnCreateSuccess({
    issueFormRef,
    handleTemplateChange,
    template,
    callback: onCreateSuccess,
  })

  useEffect(() => {
    if (repository)
      setCreateMoreCreatedPath({
        owner: repository.owner?.login || '',
        repo: repository.name || '',
        number: undefined,
      })
  }, [repository, setCreateMoreCreatedPath])

  useEffect(() => {
    if (repository !== undefined) return

    if (data) {
      onRepositorySelected(data)
    } else if (
      !optionConfig.scopedRepositoryOwner &&
      !isLoadingRepositoryData &&
      topRepositories !== 'unset' &&
      topRepositories &&
      topRepositories.length > 0
    ) {
      onRepositorySelected(topRepositories[0])
    }
  }, [
    currentRepository,
    data,
    isLoadingRepositoryData,
    onRepositorySelected,
    optionConfig,
    repository,
    topRepositories,
  ])

  let element: React.JSX.Element = (
    <div className={styles.LoadingContainer}>
      <Spinner size="large" />
    </div>
  )

  // If we are immediately bypassing the template selection, then we should allow the user to go back to choose another template or repository.
  // We don't allow users to choose another template or repository for issue duplication at the moment
  const shouldShowTemplateButton = initialDefaultDisplayMode === DisplayMode.IssueCreation

  if (displayMode === DisplayMode.IssueCreation && !isLoadingRepositoryData) {
    const IssueForm = requiresRelayEnvironment ? CreateIssueForm : CreateIssueFormBase
    element = (
      <>
        {shouldShowTemplateButton && data && <TemplatePickerButton repository={data} template={template} />}
        {repository && (
          <IssueForm
            {...props}
            issueFormRef={issueFormRef}
            onCreateSuccess={onCreateSuccessInternal}
            selectedTemplate={template}
            repository={repository}
            title={title}
            setTitle={setNewTitle}
            body={body}
            setBody={setBody}
            clearOnCreate={clearSessionData}
            focusTitleInput={!shouldShowTemplateButton}
            footer={undefined}
            duplicateDetection={duplicateDetection}
          />
        )}
      </>
    )
  } else if (displayMode === DisplayMode.IssueDuplication && !isLoadingRepositoryData) {
    element = (
      <CreateDuplicateIssueForm
        navigate={navigate}
        onCreateSuccess={onCreateSuccessInternal}
        topRepositories={topRepositories}
        issueFormRef={issueFormRef}
        {...props}
      />
    )
  } else if (
    !isLoadingRepositoryData &&
    topRepositories !== 'unset' &&
    ((displayMode === DisplayMode.TemplatePicker && optionConfig.showRepositoryPicker) ||
      (displayMode === DisplayMode.TemplatePicker && !data && !repository))
  ) {
    element = (
      <RepositoryAndTemplatePicker
        repository={repository}
        onRepositorySelected={onRepositorySelected}
        topRepositories={topRepositories}
        owner={optionConfig.scopedRepositoryOwner}
        organization={optionConfig.scopedOrganization}
        preventAutoSelect={Boolean(optionConfig.scopedRepositoryOwner)}
        onTemplateSelected={onTemplateSelected}
      />
    )
  } else if (
    displayMode === DisplayMode.TemplatePicker &&
    !optionConfig.showRepositoryPicker &&
    !isLoadingRepositoryData &&
    !!currentRepository &&
    !!data
  ) {
    element = (
      <div {...hpcAttrs()}>
        {/* RELAY-CLEANUP(CreateIssue): narrows the still-Relay CreateIssue fragment (with @include-gated,
            wider-nullable fields) to the Repository fields TemplateListInternal needs. Remove when CreateIssue leaves Relay. */}
        <TemplateListInternal
          repository={data as TemplateListPaneFragment}
          onTemplateSelected={onTemplateSelected}
          {...props}
        />
      </div>
    )
  }

  return (
    <div tabIndex={-1} className={styles.CreateIssueContainer}>
      {element}
    </div>
  )
}
